import { useEffect } from "react";

export const PHONE_DISPLAY = "(863) 225-3348";
export const PHONE_TEL = "tel:+18632253348";
export const PHONE_SMS = "sms:+18632253348";
export const EMAIL = "info@propertyresolutionsgroup.com";
export const EMAIL_MAILTO = "mailto:info@propertyresolutionsgroup.com";
export const TAGLINE = "Know your options. There's a way forward.";

export const TEAM = [
  {
    name: "Don Jerue",
    role: "REALTOR® · Partner & Co-Founder",
  },
  {
    name: "Suzie Rudd",
    role: "Short Sale Negotiator · Partner",
  },
];

export const IMAGES = {
  neighborhood:
    "https://images.unsplash.com/photo-1555636222-cae831e670b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxmbG9yaWRhJTIwbmVpZ2hib3Job29kJTIwY2FsbSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4Nzg0ODYwOXww&ixlib=rb-4.1.0&q=85",
  passage: "/hero-samples/hope-10.png",
  shelter: "/hero-samples/hope-8.png",
  roots: "/hero-samples/hope-11.png",
  porch: "/hero-samples/hope-12.png",
  key: "/hero-samples/hope-13.png",
  morning: "/hero-samples/hope-14.png",
  consultation:
    "https://images.unsplash.com/photo-1758273241078-8eec353836be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxjb21wYXNzaW9uYXRlJTIwcHJvZmVzc2lvbmFsJTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc4Nzg0ODYwOXww&ixlib=rb-4.1.0&q=85",
  texture:
    "https://images.unsplash.com/photo-1547623641-d2c56c03e2a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxjb2FzdGFsJTIwYWJzdHJhY3QlMjBzb2Z0JTIwdGV4dHVyZXxlbnwwfHx8fDE3ODc4NDg2MDl8MA&ixlib=rb-4.1.0&q=85",
};

export const COUNTIES = ["Polk", "Hillsborough", "Hardee", "Highlands"];

export const usePageTitle = (title) => {
  useEffect(() => {
    document.title = title
      ? `${title} | Property Resolutions Group`
      : "Property Resolutions Group | Compassionate Distressed Property Help in Central Florida";
  }, [title]);
};

export const situations = [
  {
    id: "short-sales",
    title: "Short Sales",
    text: "When selling for less than the balance owed is the healthiest path forward, we guide the process with your lender — discreetly and at your pace.",
  },
  {
    id: "foreclosure",
    title: "Foreclosure Concerns",
    text: "Behind on payments or received a notice? There may be more options — and more time — than you think. We help you see them clearly.",
  },
  {
    id: "inherited-vacant",
    title: "Inherited & Vacant Properties",
    text: "Probate, shared heirs, or a home sitting empty. We help families untangle the details respectfully, without rushing anyone's grief.",
  },
  {
    id: "liens",
    title: "Liens & Judgments",
    text: "Tax liens, code violations, HOA claims. We help you understand what is attached to your property and map a realistic way through.",
  },
  {
    id: "title-issues",
    title: "Title Issues",
    text: "Clouds on title, missing heirs, old mortgages that never went away. We work alongside title professionals to clear the path.",
  },
  {
    id: "other",
    title: "Other Difficult Situations",
    text: "Divorce, relocation, tired rentals, major repairs. If it feels complicated, it is exactly the kind of situation we were built for.",
  },
];

export const howWeHelp = [
  {
    num: "01",
    title: "We listen, privately",
    text: "A short, confidential conversation — by phone, text, or whatever feels comfortable. No scripts, no obligation. We simply learn what you are carrying.",
  },
  {
    num: "02",
    title: "We map your options",
    text: "We research your property and situation, then lay out every realistic path in plain language — including the ones that do not involve us at all.",
  },
  {
    num: "03",
    title: "You choose the pace",
    text: "Move forward only if and when you are ready. Whatever you decide, you will decide it with clarity — and with someone local in your corner.",
  },
];

export const faqs = [
  {
    q: "Will anyone find out I contacted you?",
    a: "No. Every conversation with PRG is confidential. We never share your information, we never put signs in your yard without permission, and neighbors will not know we spoke unless you tell them.",
  },
  {
    q: "Do I have to sell my home to you?",
    a: "No. We are a resolutions group first. Sometimes the best option is staying in your home, working with your lender, or listing traditionally. We explain every path — including the ones that do not involve us.",
  },
  {
    q: "What does a consultation cost?",
    a: "Nothing. Every consultation is free, private, and carries no obligation. You can ask questions, explore options, and never speak to us again if that is what you choose.",
  },
  {
    q: "How quickly do I need to decide?",
    a: "On your timeline, not ours. Some situations have legal deadlines — like a foreclosure sale date — and we will always be honest about those. Beyond that, you set the pace.",
  },
  {
    q: "My situation involves liens, probate, or back taxes. Can you still help?",
    a: "Yes. Layered situations are our specialty. We regularly work alongside title companies, attorneys, and lenders to untangle liens, judgments, probate, and code violations.",
  },
  {
    q: "Which areas do you serve?",
    a: "The heart of Central Florida — Polk, Hillsborough (including Tampa), Hardee, and Highlands counties, from our home base in Winter Haven. We are local, and we meet homeowners where they are.",
  },
];

export const services = [
  {
    id: "short-sales",
    title: "Short Sale Guidance",
    tagline: "Sell for less than you owe, with dignity intact.",
    description:
      "A short sale lets you sell your home for less than the remaining mortgage balance, with your lender's approval. Done well, it protects your credit far better than foreclosure and closes a difficult chapter cleanly. PRG coordinates the entire process — the lender paperwork, the negotiations, the timeline — so you never face the bank alone.",
    help: [
      "Honest assessment of whether a short sale fits your situation",
      "Direct coordination with your lender's loss mitigation team",
      "Discreet handling — no yard signs unless you want them",
    ],
    steps: [
      "A private conversation about your mortgage, hardship, and goals",
      "We prepare and submit the short sale package to your lender",
      "You review every offer and approve each decision before anything moves",
    ],
  },
  {
    id: "foreclosure",
    title: "Foreclosure Support",
    tagline: "More options, and often more time, than the letters suggest.",
    description:
      "Receiving a lis pendens or a notice of default is frightening, but it is not the end of the road. Florida's foreclosure process moves through the courts, which means there is usually a window to act — reinstate, modify, sell, or negotiate. We help you understand exactly where you stand and what each option genuinely costs.",
    help: [
      "Plain-language review of your foreclosure timeline and deadlines",
      "Exploration of loan modification, reinstatement, and sale options",
      "Coordination with attorneys and housing counselors when needed",
    ],
    steps: [
      "We review your notice and map your exact court timeline",
      "Together we compare every realistic path — keep, sell, or settle",
      "You choose; we execute alongside you until it is resolved",
    ],
  },
  {
    id: "inherited-vacant",
    title: "Inherited & Vacant Property Guidance",
    tagline: "Untangling probate and empty homes, respectfully.",
    description:
      "Inheriting a property can arrive tangled with grief, siblings, and paperwork. Whether the home needs probate, has multiple heirs, or has been sitting vacant and accruing fines, we help families understand their choices — keep, rent, or sell — and handle the logistics without pressure.",
    help: [
      "Guidance through Florida probate alongside your attorney",
      "Solutions for vacant homes with code violations or upkeep costs",
      "Options for heirs who live out of state",
    ],
    steps: [
      "A gentle conversation about the property and everyone involved",
      "We coordinate with probate attorneys and title professionals",
      "The family decides the outcome — we handle the heavy lifting",
    ],
  },
  {
    id: "liens",
    title: "Lien & Judgment Resolution",
    tagline: "Tax liens, code fines, HOA claims — mapped and resolved.",
    description:
      "Liens attach quietly and grow loudly. Property tax certificates, municipal code enforcement fines, HOA judgments, and contractor liens can all block a sale or refinance. We research exactly what is recorded against your property, negotiate reductions where possible, and build a payoff plan that clears the way.",
    help: [
      "Full lien search and plain-language explanation of each item",
      "Negotiation with municipalities, HOAs, and judgment holders",
      "Resolution paths whether you keep or sell the home",
    ],
    steps: [
      "We pull the recorded liens and explain each one in plain language",
      "We negotiate reductions and settlements on your behalf",
      "You approve the resolution plan before anything is signed",
    ],
  },
  {
    id: "title-issues",
    title: "Title Issue Navigation",
    tagline: "Clearing the clouds that keep your home in limbo.",
    description:
      "Missing heirs, unreleased old mortgages, boundary disputes, clerical errors — title problems can freeze a property for years. We work with trusted Florida title companies and real estate attorneys to diagnose the cloud, cure it, and get your property back to a clean, transferable state.",
    help: [
      "Diagnosis of title defects with partner title professionals",
      "Quiet title, curative documents, and heir resolution support",
      "Coordination through closing once the title is clear",
    ],
    steps: [
      "A title review identifies exactly what is clouding ownership",
      "We coordinate the curative work with attorneys and title agents",
      "You receive a clear path — and a clear title — before deciding next steps",
    ],
  },
  {
    id: "other",
    title: "Complex & Creative Situations",
    tagline: "If it feels too complicated, it is our kind of work.",
    description:
      "Divorce sales, sudden relocations, tired rental properties, homes needing more repair than you can fund, behind on association dues — difficult property situations rarely fit a single box. PRG exists for the tangled ones. We listen first, then design a resolution around your life, not around a transaction.",
    help: [
      "Creative solutions for situations that do not fit standard boxes",
      "Coordination with spouses, co-owners, tenants, and attorneys",
      "Fast, private resolutions when time genuinely matters",
    ],
    steps: [
      "Tell us what is happening — in whatever detail feels comfortable",
      "We design two or three realistic resolution paths for you",
      "You pick the one that fits your life; we carry it through",
    ],
  },
];

export const values = [
  {
    title: "Privacy by default",
    text: "Your situation stays between us. No signs, no shared information, no exceptions — anonymity is built into how we work.",
  },
  {
    title: "No pressure, period",
    text: "We will never push you toward a decision. Our role is to make your options clear; the choice always belongs to you.",
  },
  {
    title: "Plain-language honesty",
    text: "No jargon, no fine print, no surprises. If an option has a downside, you will hear it from us first.",
  },
  {
    title: "Local knowledge",
    text: "We live and work in Central Florida. We know these neighborhoods, these courts, and these title offices by name.",
  },
];

export const featuredStory = {
  tag: "Real PRG review · Inherited home, sold in 4 days",
  quote:
    "Don Jerue and his team did an amazing job helping us sell our parents home under difficult circumstances. We live out of state and were hoping to get the house cleaned out and ready to list in as little time as possible. Don knew just how to do that — the house sold in 4 days! Don and his team provided many resources to help us work quickly. We are forever grateful!",
  who: "An out-of-state family, selling their parents' home",
};

export const stories = [
  {
    tag: "Real PRG review · A calm, supported sale",
    quote: "I can't say enough good things about Don and his team. Selling our house was not an easy decision and my anxiety was sky-high. Don consistently calmed my nerves and answered my (many) questions and worries. I always knew he had our back 100%. Even when we hit a couple of roadblocks, he had a solution ready. The whole process went quickly and overall smoothly.",
    who: "A homeowner who was anxious about selling",
  },
  {
    tag: "Foreclosure concern · Polk County",
    quote: "We were three months behind and convinced we'd lose the house. PRG showed us options the bank never mentioned — we kept our home.",
    who: "A retired couple, Winter Haven",
  },
  {
    tag: "Real PRG review · Under contract in about 30 days",
    quote: "Don and Suzie are fantastic and they get the job done. I desperately needed to sell my brand new home that I had just bought, and it was put under contract with an excellent buyer in just around 30 days. I thank them for helping me in a bad situation. Go with this team if you need to get your house sold. They're worth it.",
    who: "A homeowner facing hardship after COVID-19",
  },
  {
    tag: "Liens & code violations · Polk County",
    quote: "Code fines had snowballed past what the house was worth — or so we thought. They negotiated the liens down and found a path we didn't know existed.",
    who: "A landlord ready to let go, Lakeland",
  },
];
